import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environment/environment';

export type DeploymentDetails = {
	deploymentId: string;
	softwareVersionId: string;
};

@Injectable({ providedIn: 'root' })
export class DeployService {
	private endpoint = environment.apiEndpoint;

	constructor(private http: HttpClient) {}

	getDeployments(
		limit?: number,
		offset?: number,
		orderColumn?: string,
		orderDirection?: 'asc' | 'desc',
		filters?: { [key: string]: any }
	): Observable<any> {
		let url = `${this.endpoint}/v1/machine/software/deploy/list`;
		const params = new URLSearchParams();

		if (limit !== undefined) params.append('limit', limit.toString());
		if (offset !== undefined) params.append('offset', offset.toString());
		if (orderColumn) params.append('orderColumn', orderColumn);
		if (orderDirection) params.append('orderDirection', orderDirection);

		if (filters) {
			Object.keys(filters).forEach((key) => {
				if (filters[key] !== null && filters[key] !== undefined) {
					if (Array.isArray(filters[key]) && filters[key].length > 0) {
						const filterObj = filters[key][0];
						if (filterObj && filterObj.value !== null && filterObj.value !== undefined && filterObj.value !== '') {
							params.append(key, filterObj.value);
						}
					} else if (
						typeof filters[key] === 'object' &&
						filters[key].value !== null &&
						filters[key].value !== undefined
					) {
						params.append(key, filters[key].value);
					} else if (typeof filters[key] === 'string') {
						params.append(key, filters[key]);
					}
				}
			});
		}

		const queryString = params.toString();
		if (queryString) {
			url += `?${queryString}`;
		}

		return this.http.get(url);
	}

	getDeploymentDetails(id: string): Observable<any> {
		return this.http.get(`${this.endpoint}/v1/machine/software/deploy?machineDeploymentId=${id}`);
	}

	getDeployLimit(): Observable<{ limit: number | null }> {
		return this.http.get<{ limit: number | null }>(`${this.endpoint}/v1/machine/software/deploy/limit`);
	}

	uploadDeployRequest(
		machines: { id: string; serial: string; family: string }[],
		softwareVersionId: string,
		updateMode?: string,
		deploymentNotes?: string,
		scheduledAt?: string,
		artifactName?: string,
		forceInstallation?: boolean
	): Observable<any> {
		const body: Record<string, any> = {
			machines,
			software_version_id: softwareVersionId,
			update_mode: updateMode || '',
			deployment_notes: deploymentNotes || '',
		};
		if (scheduledAt) {
			body['scheduled_at'] = scheduledAt;
		}
		if (artifactName) {
			body['artifact_name'] = artifactName;
		}
		if (forceInstallation) {
			body['force_installation'] = true;
		}
		return this.http.put(`${this.endpoint}/v1/machine/software/upload`, body);
	}

	cancelDeployment(deploymentId: string): Observable<any> {
		const body = {
			deployment_id: deploymentId,
		};
		return this.http.post(`${this.endpoint}/v1/machine/software/deploy/cancel`, body);
	}

	getDeploymentProgress(deployments: DeploymentDetails[]): Observable<any> {
		const params = new URLSearchParams();
		params.append('deployments', JSON.stringify(deployments));
		const queryString = params.toString();
		const url = `${this.endpoint}/v1/machine/software/deploy/progress${queryString ? `?${queryString}` : ''}`;
		return this.http.get(url);
	}
}
